import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, MessagesSquare } from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import { useNotificationStore } from '../../stores/notification-store';
import { useChatStore } from '../../stores/chat-store';
import { LanguageSwitcher } from '../language-switcher';
import { UserMenu } from './user-menu';
import { MobileMenu } from './mobile-menu';
import { useScroll } from '../../hooks/use-scroll';
import { useThemeMode } from '../../hooks/use-theme-mode';
import { HeaderSearch } from '../../features/search/components/header-search';
import { getTextDirection } from '../../lib/i18n/rtl-languages';
import type { Language } from '../../stores/app-store';


export function LandingNavbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const showAuthedChrome = isAuthenticated || !!user;
  const { unreadCount } = useNotificationStore();
  const { unreadChatIds } = useChatStore();
  const scrolled = useScroll(50);
  const themeMode = useThemeMode();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const location = useLocation();
  const navDirection = getTextDirection(i18n.language as Language);
  const unreadChats = unreadChatIds.size;
  
  const logoSrc = themeMode === 'dark' ? '/assets/images/logo-dark.png' : '/assets/images/logo-light.png';

  // Only show the skeleton on the very first load before auth state is known.
  // loadProfile() sets isLoading: true again after getSession() resolves, so
  // we must not re-trigger the skeleton on that second flip.
  useEffect(() => {
    const unsub = useAuthStore.subscribe((state) => {
      if (!state.isLoading) {
        setIsInitializing(false);
        unsub();
      }
    });
    if (!useAuthStore.getState().isLoading) {
      setIsInitializing(false);
      unsub();
    }
    return unsub;
  }, []);

  return (
    <>
      <nav
        className={`sticky top-0 flex-shrink-0 w-full h-14 md:h-20 z-50 transition-all duration-300 ${scrolled
          ? 'border-b border-border bg-background/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/95'
          : 'bg-background/60 backdrop-blur supports-[backdrop-filter]:bg-background/60'
          }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex h-14 md:h-20 items-center justify-between gap-2 md:gap-4">
            {/* Logo */}
            <Link
              to="/"
              className={`flex items-center group relative flex-shrink-0 ${scrolled ? 'drop-shadow-[0_0_12px_rgba(251,146,60,0.4)]' : ''
                }`}
            >
              <img
                src={logoSrc}
                alt="Candle Legacy"
                className="h-10 md:h-16 lg:h-20 w-auto max-w-[160px] md:max-w-[240px] lg:max-w-[320px] object-contain transition-all duration-300 ease-out group-hover:scale-105 group-hover:drop-shadow-[0_0_15px_rgba(251,146,60,0.6)] filter brightness-100 group-hover:brightness-110"
              />
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/20 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl -z-10" />
            </Link>

            {/* Middle section: Nav + Search */}
            <div
              className={`hidden md:flex items-center gap-4 flex-grow ${navDirection === 'rtl' ? 'flex-row-reverse' : ''}`}
              dir={navDirection}
            >
              {/* Desktop Navigation */}
              <nav className="flex items-center gap-4">
                {showAuthedChrome ? (
                  // Authenticated users always see their app nav links
                  <>
                    <Link
                      to="/home"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('navbar.home')}
                    </Link>
                    <Link
                      to="/my-pages"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('nav.mySpace', 'My Space')}
                    </Link>
                    <Link
                      to="/chats"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('navbar.messages')}
                    </Link>
                  </>
                ) : (
                  // Unauthenticated users see the public/marketing links
                  <>
                    <Link
                      to="/"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('navbar.home')}
                    </Link>
                    <Link
                      to="/honored"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('navbar.honored', 'Honored lives')}
                    </Link>
                    <Link
                      to="/about"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('navbar.about')}
                    </Link>
                    <Link
                      to="/features"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('navbar.features')}
                    </Link>
                    <Link
                      to="/partners"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('navbar.partners', 'Partners')}
                    </Link>
                    <Link
                      to="/contact"
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {t('navbar.contact')}
                    </Link>
                  </>
                )}
              </nav>

              {/* Search Bar - visible for authenticated users */}
              {showAuthedChrome && <HeaderSearch />}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4">
              <div className="hidden md:block">
                <LanguageSwitcher compact />
              </div>

              {/* Auth Buttons / User Menu */}
              {isInitializing ? (
                <div className="hidden h-9 w-24 animate-pulse rounded bg-muted md:block" />
              ) : showAuthedChrome ? (
                <>
                  {/* Messages icon */}
                  <button
                    onClick={() => navigate('/chats')}
                    className="relative rounded-full p-2 text-foreground hover:bg-accent transition-colors"
                    aria-label={t('navbar.messages', 'Messages')}
                  >
                    <MessagesSquare className="h-5 w-5" />
                    {unreadChats > 0 && (
                      <span className="absolute end-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                        {unreadChats > 9 ? '9+' : unreadChats}
                      </span>
                    )}
                  </button>

                  {/* Notifications icon */}
                  <button
                    onClick={() => navigate('/notifications')}
                    className="relative rounded-full p-2 text-foreground hover:bg-accent transition-colors"
                    aria-label={t('navbar.notifications', 'Notifications')}
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute end-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  <div className="hidden md:block">
                    <UserMenu key={`user-menu-${user?.id || 'guest'}`} />
                  </div>
                  {/* Mobile Menu Button - for authenticated users */}
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="md:hidden rounded-lg p-2 text-foreground hover:bg-accent transition-colors"
                    aria-label={t('common.openMenu', 'Open menu')}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/sign-in"
                    className="hidden md:inline text-sm font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap"
                  >
                    {t('common.signIn')}
                  </Link>
                  <Link
                    to="/sign-up"
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors whitespace-nowrap md:px-4 md:py-2 md:text-sm"
                  >
                    {t('common.signUp')}
                  </Link>
                  {/* Mobile Menu Button - for unauthenticated users */}
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="md:hidden rounded-lg p-2 text-foreground hover:bg-accent transition-colors"
                    aria-label={t('common.openMenu', 'Open menu')}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
    </>
  );
}
